import { useEffect, useRef } from 'react'
import type { TouchEvent as ReactTouchEvent } from 'react'
import { INNER_PLANETS } from '../lib/innerPlanets'
import { lockBodyScroll, unlockBodyScroll } from '../lib/bodyScroll'
import { useIsMobile } from '../lib/useMobile'
import type { InterstellarObject } from '../lib/types'

type SidebarProps = {
  items: InterstellarObject[]
  closest?: Array<{ id: string; name: string; date?: string; dist_ld?: number | null }>
  selectedId: string | null
  onSelect: (id: string) => void
  onSelectClosest?: (id: string) => void
  onSelectSpecial: (id: string) => void
  className?: string
  loading?: boolean
  error?: string | null
  onRetry?: () => void
  open?: boolean
  onClose?: () => void
}

export default function Sidebar({
  items,
  closest = [],
  selectedId,
  onSelect,
  onSelectClosest,
  onSelectSpecial,
  className = '',
  loading = false,
  error = null,
  onRetry,
  open = true,
  onClose,
}: SidebarProps) {
  const isMobile = useIsMobile()
  const panelRef = useRef<HTMLElement | null>(null)
  const startXRef = useRef(0)
  const deltaXRef = useRef(0)

  useEffect(() => {
    if (!isMobile) return
    if (open) {
      lockBodyScroll()
      return () => unlockBodyScroll()
    }
    unlockBodyScroll()
    return undefined
  }, [isMobile, open])

  useEffect(() => () => unlockBodyScroll(), [])

  const handleTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!isMobile || !open) return
    startXRef.current = event.touches[0]?.clientX ?? 0
    deltaXRef.current = 0
    if (panelRef.current) {
      panelRef.current.style.transition = 'none'
    }
  }

  const handleTouchMove = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!isMobile || !open) return
    const currentX = event.touches[0]?.clientX ?? 0
    const delta = currentX - startXRef.current
    deltaXRef.current = delta
    if (delta > 0 && panelRef.current) {
      event.preventDefault()
      panelRef.current.style.transform = `translateX(${delta}px)`
    }
  }

  const handleTouchEnd = () => {
    if (!isMobile || !open) return
    const panel = panelRef.current
    if (panel) {
      panel.style.transition = ''
      panel.style.transform = ''
    }
    if (deltaXRef.current > 80) {
      onClose?.()
    }
    deltaXRef.current = 0
  }

  const panelClasses = [
    'fixed md:static inset-y-0 left-0 md:inset-auto z-40 md:z-auto',
    'flex flex-col',
    'w-[86vw] max-w-[420px] md:w-80',
    'bg-black/60 md:bg-transparent',
    'backdrop-blur md:backdrop-blur-0',
    'border-r border-white/10 md:border-none',
    'pt-[calc(env(safe-area-inset-top)+12px)]',
    'pb-[calc(env(safe-area-inset-bottom)+12px)]',
    'px-4',
    'transition-transform duration-300 ease-out md:transition-none',
    open ? 'translate-x-0' : '-translate-x-[95vw]',
    'md:translate-x-0',
  ]
    .filter(Boolean)
    .join(' ')

  const contentClasses = ['flex h-full flex-col gap-4', className].filter(Boolean).join(' ')

  return (
    <>
      {isMobile && open && (
        <button
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          aria-label="Close navigation"
          type="button"
        />
      )}
      <aside
        ref={panelRef}
        className={panelClasses}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <div className={contentClasses}>
          <div>
            <h2 className="text-xs uppercase tracking-[0.24em] text-neutral-500">Solar System</h2>
            <ul className="mt-2 space-y-1.5">
              {INNER_PLANETS.map((planet) => (
                <li key={planet.id}>
                  <button
                    type="button"
                    onClick={() => onSelectSpecial(planet.id)}
                    className="group flex w-full items-center justify-between rounded-lg border border-white/5 bg-white/5 px-3 py-2 text-left transition hover:border-sky-500/40 hover:bg-white/10"
                  >
                    <span className="text-sm font-medium text-slate-100">{planet.name}</span>
                    <span className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 group-hover:text-neutral-300">
                      planet
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-xs uppercase tracking-[0.24em] text-neutral-500">
              Interstellar Objects
            </h2>
            <span className="rounded-full border border-neutral-800 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
              ISO
            </span>
          </div>

          <div className="flex-1 overflow-y-auto pr-1">
            {loading && (
              <div className="space-y-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="animate-pulse rounded-xl border border-white/5 bg-white/5 px-4 py-4"
                  >
                    <div className="h-4 w-3/5 rounded bg-white/10" />
                    <div className="mt-3 h-3 w-2/5 rounded bg-white/5" />
                  </div>
                ))}
              </div>
            )}

            {!loading && error && (
              <div className="space-y-3 rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-200">
                <p>We lost contact with SBDB. Try again shortly.</p>
                {onRetry && (
                  <button
                    type="button"
                    onClick={onRetry}
                    className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.24em] text-rose-100 transition hover:border-white/40"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}

            {!loading && !error && items.length === 0 && (
              <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-6 text-sm text-neutral-400">
                No interstellar visitors are available yet.
              </div>
            )}

            {!loading && !error && items.length > 0 && (
              <ul className="flex flex-col gap-1.5">
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(item.id)}
                      className={[
                        'flex w-full items-center justify-between rounded-lg border border-white/5 px-3 py-2 text-left transition duration-200 ease-standard',
                        'bg-ink-2/30 hover:border-sky-400/60 hover:bg-ink-2/60 hover:drop-glow',
                        selectedId === item.id ? 'border-sky-500/70 bg-ink-2/80 shadow-glow' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <span className="text-sm font-semibold text-slate-100">{item.name}</span>
                      <span className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                        {item.id}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {!loading && !error && closest.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 flex items-center justify-between">
                  <h2 className="text-[11px] uppercase tracking-wide text-neutral-400">
                    Closest Approaches
                  </h2>
                  <span className="rounded-full border border-neutral-800 px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                    CAD
                  </span>
                </div>
                <ul className="flex flex-col gap-1">
                  {closest.map((item) => (
                    <li key={`${item.id}-${item.date ?? 'nodate'}`}>
                      <button
                        type="button"
                        onClick={() => onSelectClosest?.(item.id)}
                        className="w-full rounded-lg border border-white/5 px-3 py-2 text-left transition hover:border-sky-400/40 hover:bg-ink-2/60"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-100">{item.name}</span>
                          <span className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">
                            {item.dist_ld ? `${item.dist_ld} LD` : ''}
                          </span>
                        </div>
                        {item.date && (
                          <div className="mt-1 text-[11px] text-neutral-500">{item.date}</div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}
